import { type ReactNode } from "react";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Modal, Show } from "shared/components";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Pagination } from "shared/components/Pagination";
import { cx } from "shared/utils";

export const AnalyticsDetailModal = ({
  opened,
  onClose,
  title,
  subtitle,
  gridClassName,
  columns,
  isLoading,
  isFetching,
  isEmpty,
  emptyText,
  children,
  pagination,
  panelClassName = "max-w-[822px]",
  headerExtra,
  footerLeft,
  hidePaginationEdges = false,
  showColumns = true,
  showPagination = true
}: {
  opened: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  gridClassName: string;
  columns: { label: string; className?: string }[];
  isLoading: boolean;
  isFetching: boolean;
  isEmpty: boolean;
  emptyText?: string;
  children: ReactNode;
  pagination: {
    total: number;
    current: number;
    onChange: (page: number) => void;
  };
  panelClassName?: string;
  headerExtra?: ReactNode;
  footerLeft?: ReactNode;
  hidePaginationEdges?: boolean;
  showColumns?: boolean;
  showPagination?: boolean;
}) => (
  <Modal
    opened={opened}
    onClose={onClose}
    panelClassName={cx(
      "flex flex-col w-full h-[652px] max-h-[90vh]",
      panelClassName
    )}
  >
    <div className="flex items-start justify-between border-b border-alpha-40 pl-6 pr-4 pt-5 pb-4">
      <div>
        <h2 className="text-xl font-bold text-content-1000">{title}</h2>
        <Show if={Boolean(subtitle)}>
          <p className="mt-1 text-sm font-medium text-content-300">
            {subtitle}
          </p>
        </Show>
      </div>
      <IconButton
        HugeIcon={Cancel01Icon}
        size={IconButtonSize.SM}
        onClick={onClose}
        variant={IconButtonVariant.TEXT_BASE}
      />
    </div>
    <Show if={Boolean(headerExtra)}>
      <div className="border-b border-alpha-40 pl-6 pr-4 py-4">
        {headerExtra}
      </div>
    </Show>

    <div className="flex-1 overflow-y-auto min-h-0">
      {showColumns ? (
        <div className="sticky top-0 z-10 bg-content-1000-inverted pl-6 pt-4">
          <div className={cx(gridClassName, "border-b border-alpha-40 pb-3")}>
            {columns.map((col) => (
              <span
                key={col.label}
                className={cx(
                  "text-sm font-medium text-content-300",
                  col.className
                )}
              >
                {col.label}
              </span>
            ))}
          </div>
        </div>
      ) : null}
      {isLoading ? (
        <div className="px-6 py-8 text-center text-sm text-content-300">
          Loading...
        </div>
      ) : (
        <div
          className={cx("pl-6", isFetching && "opacity-50 pointer-events-none")}
        >
          {isEmpty ? (
            <div className="py-8 text-center text-sm text-content-300">
              {emptyText}
            </div>
          ) : (
            children
          )}
        </div>
      )}
    </div>
    <Show if={Boolean(showPagination && !isEmpty && !isLoading)}>
      <div className="flex items-center justify-between gap-4 border-t border-alpha-40 pl-6 pr-4 py-3">
        <span className="whitespace-nowrap text-base font-medium text-content-1000">
          {footerLeft}
        </span>
        <Pagination
          className="w-auto justify-end"
          total={pagination.total}
          current={pagination.current}
          onChange={pagination.onChange}
          disabled={isFetching}
          showEdgeButtons={!hidePaginationEdges}
        />
      </div>
    </Show>
  </Modal>
);
